import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { 
  getTeacherWeeklySchedule,
  getUnavailableTime,
  setUnavailableTime,
  getWeekdays,
  getWeeklyCalendarTemplates,
  createWeeklyCalendarTemplate,
  updateWeeklyCalendarTemplate,
  deleteWeeklyCalendarTemplate
} from '@/api/schedule'

export const useScheduleStore = defineStore('schedule', () => {
  const schedules = ref([])
  const weeklySchedule = ref([])
  const unavailableTimes = ref([])
  const weekdays = ref([])
  const weeklyCalendarTemplates = ref([])
  const loading = ref(false)
  const total = ref(0)
  
  const filters = ref({
    teacherId: null
  })

  const availableSchedules = computed(() => 
    schedules.value.filter(s => s.status === 'available')
  )
  
  const bookedSchedules = computed(() => 
    schedules.value.filter(s => s.status === 'booked')
  )

  const schedulesCount = computed(() => schedules.value.length)

  const fetchWeeklySchedule = async (teacherId, params = {}) => {
    loading.value = true
    try {
      const res = await getTeacherWeeklySchedule(teacherId, params)
      
      if (res.success) {
        // Handle different response structures
        if (Array.isArray(res.data)) {
          // API returns data directly as array: { success: true, data: [...] }
          weeklySchedule.value = res.data
        } else if (res.data?.list) {
          // API returns data.list: { success: true, data: { list: [...] } }
          weeklySchedule.value = res.data.list
        } else if (res.data && typeof res.data === 'object') {
          // API returns single object: { success: true, data: {...} }
          weeklySchedule.value = [res.data]
        } else {
          weeklySchedule.value = []
        }
        
        return { success: true, data: weeklySchedule.value }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Schedule Store] Fetch weekly schedule error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const fetchUnavailableTime = async () => {
    loading.value = true
    try {
      const res = await getUnavailableTime()
      
      if (res.success) {
        unavailableTimes.value = res.data || []
        return { success: true, data: unavailableTimes.value }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Schedule Store] Fetch unavailable time error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const saveUnavailableTime = async (data) => {
    loading.value = true
    try {
      const res = await setUnavailableTime(data)
      
      if (res.success) {
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Schedule Store] Save unavailable time error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const fetchWeekdays = async () => {
    loading.value = true
    try {
      const res = await getWeekdays()
      
      if (res.success) {
        weekdays.value = res.data || []
        return { success: true, data: weekdays.value }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Schedule Store] Fetch weekdays error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  // Weekly Calendar Template actions
  const fetchWeeklyCalendarTemplates = async () => {
    loading.value = true
    try {
      const res = await getWeeklyCalendarTemplates()
      
      if (res.success) {
        weeklyCalendarTemplates.value = res.data || []
        return { success: true, data: weeklyCalendarTemplates.value }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Schedule Store] Fetch weekly calendar templates error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const saveWeeklyCalendarTemplate = async (data) => {
    loading.value = true
    try {
      const res = await createWeeklyCalendarTemplate(data)
      
      if (res.success) {
        // Refresh the list
        await fetchWeeklyCalendarTemplates()
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Schedule Store] Save weekly calendar template error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const updateWeeklyCalendarTemplateAction = async (dayOfWeek, data) => {
    loading.value = true
    try {
      const res = await updateWeeklyCalendarTemplate(dayOfWeek, data)
      
      if (res.success) {
        // Refresh the list
        await fetchWeeklyCalendarTemplates()
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Schedule Store] Update weekly calendar template error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const removeWeeklyCalendarTemplate = async (dayOfWeek) => {
    loading.value = true
    try {
      const res = await deleteWeeklyCalendarTemplate(dayOfWeek)
      
      if (res.success) {
        // Remove from local state
        weeklyCalendarTemplates.value = weeklyCalendarTemplates.value.filter(
          t => t.dayOfWeek !== dayOfWeek
        )
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Schedule Store] Delete weekly calendar template error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  return {
    schedules,
    weeklySchedule,
    unavailableTimes,
    weekdays,
    weeklyCalendarTemplates,
    loading,
    total,
    filters,
    availableSchedules,
    bookedSchedules,
    schedulesCount,
    fetchWeeklySchedule,
    fetchUnavailableTime,
    saveUnavailableTime,
    fetchWeekdays,
    fetchWeeklyCalendarTemplates,
    saveWeeklyCalendarTemplate,
    updateWeeklyCalendarTemplateAction,
    removeWeeklyCalendarTemplate
  }
})
